import { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { CheckIcon, PencilIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { toast } from 'react-toastify';
import { effectiveCapitalApi } from '../../helpers/effectiveCapitalApi';
import { formatCurrencyFull } from '../../helpers/effectiveCapitalFormat';
import { LoaderSpinner } from '../LoaderSpinner';

function NicknameCell({ acct, isAdmin, onSave }) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(acct.customName ?? acct.name);
  const displayName = acct.customName ?? acct.name;

  const startEdit = () => {
    setValue(acct.customName ?? acct.name);
    setEditing(true);
  };

  const save = async () => {
    await onSave(acct.plaidAccountId, value);
    setEditing(false);
  };

  const cancel = () => {
    setValue(acct.customName ?? acct.name);
    setEditing(false);
  };

  if (editing) {
    return (
      <div className="flex items-center gap-1">
        <input
          autoFocus
          type="text"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') save();
            if (e.key === 'Escape') cancel();
          }}
          className="w-44 border-b border-gray-900 bg-blue-50 text-sm focus:outline-none"
        />
        <button type="button" onClick={save} className="text-gray-900 hover:opacity-70" title="Save">
          <CheckIcon className="w-3 h-3 shrink-0" />
        </button>
        <button type="button" onClick={cancel} className="text-gray-500 hover:opacity-70" title="Cancel">
          <XMarkIcon className="w-3 h-3 shrink-0" />
        </button>
      </div>
    );
  }

  return (
    <div className="group flex items-center gap-1.5">
      <span>{displayName}</span>
      {acct.mask && <span className="text-xs text-gray-500">···{acct.mask}</span>}
      {acct.customName && acct.customName !== acct.name && (
        <span className="text-xs text-gray-500">({acct.name})</span>
      )}
      {isAdmin && (
        <button
          type="button"
          onClick={startEdit}
          className="opacity-30 hover:opacity-80 transition-opacity"
          title="Edit nickname"
        >
          <PencilIcon className="w-3 h-3 shrink-0" />
        </button>
      )}
    </div>
  );
}

function EntityCell({ acct, isAdmin, onSave }) {
  return (
    <div className="mt-1 flex items-center gap-2 text-[11px]">
      <label className="text-gray-500">Entity</label>
      <select
        value={acct.entity}
        disabled={!isAdmin}
        onChange={(e) => onSave(acct.plaidAccountId, e.target.value)}
        className="border border-gray-200 bg-white px-1 py-0.5 text-[11px] disabled:bg-gray-50 disabled:text-gray-500"
      >
        <option value="kings_loot">Kings Loot</option>
        <option value="kings_holding">Kings Holding</option>
      </select>
    </div>
  );
}

function isExcluded(acct) {
  return acct.excludeFromTotals === true || Number(acct.excludeFromTotals) === 1;
}

function ExcludeCell({ acct, isAdmin, onSave }) {
  const excluded = isExcluded(acct);
  return (
    <label className="mt-1 flex items-center gap-1.5 text-[11px] text-gray-500">
      <input
        type="checkbox"
        checked={excluded}
        disabled={!isAdmin}
        onChange={(e) => onSave(acct.plaidAccountId, e.target.checked)}
      />
      <span>{excluded ? 'Excluded from totals' : 'Exclude from totals'}</span>
    </label>
  );
}

function PaymentDueCell({ acct, isAdmin, onSave }) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(String(acct.paymentDueDay ?? ''));
  const isCC = acct.type === 'credit';

  if (!isCC) return '—';
  if (!isAdmin) return acct.paymentDueDate ?? '—';

  if (editing) {
    return (
      <div className="flex items-center justify-end gap-1">
        <input
          type="number"
          min={1}
          max={31}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') onSave(acct.plaidAccountId, value);
            if (e.key === 'Escape') setEditing(false);
          }}
          className="w-12 border border-gray-300 px-1 py-0.5 text-xs text-right focus:outline-none"
          placeholder="Day"
        />
        <button type="button" onClick={() => onSave(acct.plaidAccountId, value)} className="text-xs font-medium hover:underline">Save</button>
        <button type="button" onClick={() => setEditing(false)} className="text-xs text-gray-500 hover:underline">Cancel</button>
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={() => {
        setValue(String(acct.paymentDueDay ?? ''));
        setEditing(true);
      }}
      className="text-sm hover:underline"
    >
      {acct.paymentDueDate ?? 'Set due date'}
    </button>
  );
}

export const EffectiveCapitalSummaryAccounts = ({ onNavigateTab, onLoadingChange, viewSwitcher }) => {
  const { currentUser } = useSelector((state) => state.auth);
  const isAdmin = currentUser?.role === 'ADMIN' || currentUser?.role === 'BOOKKEEPER' || currentUser?.role === 'FINANCE';
  const [accounts, setAccounts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const loadAccounts = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await effectiveCapitalApi.listAccounts(true);
      setAccounts(data);
    } catch (error) {
      toast.error(error?.response?.data?.error || 'Failed to load accounts', { position: 'bottom-right' });
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAccounts();
  }, [loadAccounts]);

  useEffect(() => {
    onLoadingChange?.(isLoading);
  }, [isLoading, onLoadingChange]);

  const savePaymentDue = async (plaidAccountId, dueDayInput) => {
    const day = parseInt(dueDayInput, 10);
    if (day < 1 || day > 31) return;
    try {
      await effectiveCapitalApi.updatePaymentDue(plaidAccountId, day);
      toast.success('Payment due date updated', { position: 'bottom-right' });
      loadAccounts();
    } catch (error) {
      toast.error(error?.response?.data?.error || 'Failed to update payment due', { position: 'bottom-right' });
    }
  };

  const saveNickname = async (plaidAccountId, customName) => {
    try {
      await effectiveCapitalApi.updateCustomName(plaidAccountId, customName);
      toast.success('Account nickname saved', { position: 'bottom-right' });
      loadAccounts();
    } catch (error) {
      toast.error(error?.response?.data?.error || 'Failed to save nickname', { position: 'bottom-right' });
    }
  };

  const saveEntity = async (plaidAccountId, entity) => {
    try {
      await effectiveCapitalApi.updateEntity(plaidAccountId, entity);
      toast.success('Account entity updated', { position: 'bottom-right' });
      await loadAccounts();
    } catch (error) {
      toast.error(error?.response?.data?.error || 'Failed to update account entity', { position: 'bottom-right' });
    }
  };

  const saveExcludeFromTotals = async (plaidAccountId, excludeFromTotals) => {
    try {
      await effectiveCapitalApi.setExcludeFromTotals(plaidAccountId, excludeFromTotals);
      toast.success(excludeFromTotals ? 'Account excluded from totals' : 'Account included in totals', { position: 'bottom-right' });
      await loadAccounts();
    } catch (error) {
      toast.error(error?.response?.data?.error || 'Failed to update account totals exclusion', { position: 'bottom-right' });
    }
  };

  const setRetirement = async (acct, retired) => {
    const accountName = acct.customName ?? acct.name;
    const noun = acct.type === 'credit' ? 'card' : 'account';
    const confirmed = window.confirm(
      retired
        ? `Retire ${accountName}? This ${noun} will stay connected and keep syncing, but it will be hidden from ECR, Bank Balances, and totals.`
        : `Restore ${accountName}? This ${noun} will be shown in ECR, Bank Balances, and totals again.`
    );
    if (!confirmed) return;
    try {
      await effectiveCapitalApi.setAccountRetired(acct.plaidAccountId, retired);
      toast.success(retired ? 'Account retired' : 'Account restored', { position: 'bottom-right' });
      loadAccounts();
    } catch (error) {
      toast.error(error?.response?.data?.error || 'Failed to update account retirement', { position: 'bottom-right' });
    }
  };

  const activeAccounts = accounts.filter((a) => !a.retiredAt);
  const retiredAccounts = accounts.filter((a) => a.retiredAt);
  const klAccounts = activeAccounts.filter((a) => a.entity === 'kings_loot');
  const khAccounts = activeAccounts.filter((a) => a.entity === 'kings_holding');

  const AccountTable = ({ rows, label, retired = false }) => (
    <div className="mb-6">
      <div className="entity-header mb-2">{label}</div>
      <div className="overflow-x-auto">
        <table className="eadin-table">
          <thead>
            <tr>
              <th>Account Name</th>
              <th>Type</th>
              <th className="!text-right">Available Balance</th>
              <th className="!text-right">Current Balance</th>
              <th className="!text-right">Credit Limit</th>
              <th className="!text-right">Remaining Limit</th>
              <th className="!text-right">Payment Due</th>
              <th className="!text-right">Last Synced</th>
              {isAdmin && <th className="!text-right">Actions</th>}
            </tr>
          </thead>
          <tbody>
            {rows.map((acct) => {
              const avail = parseFloat(String(acct.availableBalance ?? 0));
              const current = parseFloat(String(acct.currentBalance ?? 0));
              const limit = acct.creditLimit ? parseFloat(String(acct.creditLimit)) : null;
              const remaining = limit !== null ? limit - current : null;
              const isCC = acct.type === 'credit';

              return (
                <tr key={acct.id} className={retired ? 'text-gray-400 bg-gray-50' : ''}>
                  <td className="px-3 py-2 text-sm font-medium">
                    <NicknameCell acct={acct} isAdmin={isAdmin} onSave={saveNickname} />
                    <EntityCell acct={acct} isAdmin={isAdmin} onSave={saveEntity} />
                    <ExcludeCell acct={acct} isAdmin={isAdmin} onSave={saveExcludeFromTotals} />
                    {isExcluded(acct) && (
                      <span className="mt-1 inline-block text-[9px] font-medium uppercase tracking-wide text-amber-600">
                        Excluded
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-xs text-gray-500 capitalize">{acct.subtype ?? acct.type}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-sm">{formatCurrencyFull(avail)}</td>
                  <td className={`px-3 py-2 text-right tabular-nums text-sm ${isCC ? 'value-negative' : ''}`}>
                    {isCC ? `(${formatCurrencyFull(current)})` : formatCurrencyFull(current)}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums text-sm">{limit !== null ? formatCurrencyFull(limit) : '—'}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-sm">{remaining !== null ? formatCurrencyFull(remaining) : '—'}</td>
                  <td className="px-3 py-2 text-right text-sm">
                    <PaymentDueCell acct={acct} isAdmin={isAdmin} onSave={savePaymentDue} />
                  </td>
                  <td className="px-3 py-2 text-right text-xs text-gray-500 tabular-nums">
                    {acct.lastSyncedAt
                      ? new Date(acct.lastSyncedAt).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })
                      : '—'}
                  </td>
                  {isAdmin && (
                    <td className="px-3 py-2 text-right text-xs">
                      <button
                        type="button"
                        onClick={() => setRetirement(acct, !retired)}
                        className="font-medium text-gray-600 hover:underline"
                      >
                        {retired ? 'Restore' : 'Retire'}
                      </button>
                    </td>
                  )}
                </tr>
              );
            })}
            {rows.length === 0 && (
              <tr>
                <td colSpan={isAdmin ? 9 : 8} className="px-3 py-6 text-center text-sm text-gray-500">No accounts for {label}</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );

  return (
    <div className="flex flex-col">
      <div className="flex shrink-0 items-center justify-between py-3 border-b border-gray-200 mb-4">
        <div>
          {viewSwitcher || <h2 className="text-sm font-bold uppercase tracking-widest">Accounts</h2>}
          <p className="text-xs text-gray-500">
            {activeAccounts.length} accounts connected{isAdmin ? ' — click the pencil icon to rename' : ''}
          </p>
        </div>
      </div>

      {isLoading ? (
        <div className="flex h-32 items-center justify-center text-sm text-gray-500 mt-10">
          <LoaderSpinner height="80" width="80" colorSpinner="black" border="4" />
        </div>
      ) : accounts.length === 0 ? (
        <div className="flex h-32 flex-col items-center justify-center gap-2 text-sm text-gray-500">
          <p>No accounts connected yet.</p>
          {isAdmin && (
            <button type="button" onClick={() => onNavigateTab?.('connect-accounts')} className="text-xs underline">
              Connect accounts
            </button>
          )}
        </div>
      ) : (
        <>
          <AccountTable rows={klAccounts} label="Kings Loot" />
          <AccountTable rows={khAccounts} label="Kings Holding" />
          {retiredAccounts.length > 0 && (
            <AccountTable rows={retiredAccounts} label="Retired" retired />
          )}
        </>
      )}
    </div>
  );
};
